import React from 'react';

import {
  useColorModeValue,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Icon,
  Text,
  Box,
  Button,
  HStack,
  Td,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  PopoverHeader,
  Heading,
  Stack,
  useDisclosure,
  Link,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiChevronDown } from 'react-icons/bi';
import { FiHelpCircle } from 'react-icons/fi';

import { useIsDatasetComparisonEnabled } from 'components/shared/Compare';
import useAllIndicators from 'hooks/data/datasets/lcia/useAllIndicators';
import { AccessResourceKey } from 'hooks/data/files/types';
import { Product } from 'hooks/data/search/types';
import useAccess from 'hooks/useAccess';
import useVersionInfo from 'hooks/useVersionInfo';
import { newerVersions } from 'pages/Search/utils';
import { checkAccessToResource } from 'utilities/auth/access';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { buildDefaultSelectedLciaMethod, selectedMethodToString } from '../../SearchPreferencesModalContent/utils';

import ActivityRowGroup from './ActivityRowGroup';

type Props = {
  product: Product;
  resultPosition: number;
};

const ProductSearchResultTable = ({ product, resultPosition }: Props) => {
  const thGray = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const iconColor = useColorModeValue('green.500', 'green.300');

  const { data: indicators } = useAllIndicators();
  const { ver, system_model } = useVersionInfo();
  const { isOpen, onClose, onToggle } = useDisclosure();
  const isCompareEnabled = useIsDatasetComparisonEnabled();

  const selectedMethod = indicators && buildDefaultSelectedLciaMethod(indicators, ver, system_model);

  const { data: accessData } = useAccess();

  const [displayCount, setDisplayCount] = React.useState(10);

  const canSeeScores = checkAccessToResource(accessData, AccessResourceKey.SCORES, ver);

  const onClosePopover = () => {
    mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLOSED, { info: 'Impact Score' });
    onClose();
  };

  return (
    <TableContainer whiteSpace={'auto'}>
      <Table variant="simple" fontSize={'md'} size="sm">
        <Thead borderColor={borderColor}>
          <Tr>
            <Th borderColor={borderColor} color={thGray} maxW={{ base: '100%', xl: '400px' }} py={2}>
              Activity & Type
            </Th>
            <Th borderColor={borderColor} color={thGray}>
              Geography
            </Th>
            {canSeeScores && newerVersions.includes(ver) && (
              <Th justifyContent={'flex-end'} borderColor={borderColor} color={thGray} whiteSpace={'nowrap'} isNumeric pr={14}>
                <Text as="span">Impact Score</Text>
                <Popover isOpen={isOpen} onClose={onClosePopover} strategy="fixed">
                  <PopoverTrigger>
                    <Box p={0} verticalAlign={'middle'} ml={2} display={'inline-block'} cursor={'pointer'}>
                      <Icon
                        mt="2px"
                        as={FiHelpCircle}
                        color={iconColor}
                        onClick={() => {
                          mixpanel.track(MIXPANEL_EVENTS.INFO_POPOVER_CLICKED, { info: 'Impact Score' });
                          onToggle();
                        }}
                      />
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent px={6} py={5}>
                    <PopoverArrow />
                    <PopoverHeader borderBottom="none" p={0}>
                      <Heading size="sm">Impact Score</Heading>
                    </PopoverHeader>
                    <PopoverBody p={0} mt={4}>
                      <Stack spacing={3}>
                        <Text>
                          The environmental impact score for this dataset, calculated using a specific method and indicator
                        </Text>
                        <Text>
                          Currently showing:{' '}
                          <Text as="span" fontWeight="bold">
                            {selectedMethod && selectedMethodToString(selectedMethod)}
                          </Text>
                          — this is the carbon footprint in kg CO2-equivalents.
                        </Text>
                        <Text>
                          You can change which impact you want to see (like water use or toxicity) in the "Preferences" control
                          on this page.
                        </Text>
                      </Stack>
                    </PopoverBody>
                    <Stack flexDir="row" justify="flex-end" mt={4} spacing={2}>
                      <Button width={{ base: '100%', md: 'auto' }} onClick={onClose} variant="ghost">
                        Close
                      </Button>
                      <Button
                        width={{ base: '100%', md: 'auto' }}
                        onClick={() => {
                          mixpanel.track(MIXPANEL_EVENTS.LEARN_MORE_INFO_POPOVER_CLICKED, { info: 'Impact Score' });
                          onClose();
                        }}
                        as={Link}
                        isExternal
                        sx={{ '&:hover': { textDecoration: 'none' } }}
                        href={'https://support.ecoinvent.org/lcia-results'}
                      >
                        Learn More
                      </Button>
                    </Stack>
                  </PopoverContent>
                </Popover>
              </Th>
            )}
            <Th w={isCompareEnabled ? '220px' : '150px'} borderColor={borderColor}>
              Dataset
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {product.activities.slice(0, displayCount).map((activity, activityIndex) => (
            <>
              <ActivityRowGroup
                activity={activity}
                key={activityIndex}
                isLastGroup={activityIndex === product.activities.length - 1 || activityIndex === displayCount - 1}
                resultPosition={resultPosition}
                productName={product.name}
                unit={product.unit}
              />
              {displayCount < product.activities.length && activityIndex === displayCount - 1 && (
                <Tr>
                  <Td colSpan={4} border="none">
                    <HStack justifyContent={'center'} py={2}>
                      <Button
                        variant={'link'}
                        rightIcon={<Icon as={BiChevronDown} />}
                        onClick={() => setDisplayCount((count) => count * 2)}
                      >
                        Show more activities ({product.activities.length - displayCount} more)
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              )}
            </>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default ProductSearchResultTable;
